import { collection, doc, onSnapshot } from "firebase/firestore";
import { useEffect, useEffectEvent, useReducer, useRef } from "react";
import UserProfile, { userProfileConverter } from "./UserProfile";
import { getFirestoreDB } from "@/firebase";
import { Unsubscribe, User } from "firebase/auth";

type State<T> =
    | { status: "loading" | "success" | "init"; value: T | null; retries: number }
    | { status: "error" | "failure"; value: null; error: unknown; retries: number };
type Action<T> =
    | { type: "start" }
    | { type: "success"; value: T }
    | { type: "error"; error: unknown }
    | { type: "failure"; error: unknown };

function reducer<T>(state: State<T>, action: Action<T>): State<T> {
    switch (action.type) {
        case "start":
            return { status: "loading", value: state.value ?? null, retries: state.retries };
        case "success":
            return { status: "success", value: action.value, retries: 0 };
        case "error":
            return { status: "error", value: null, error: action.error, retries: state.retries + 1 };
        case "failure":
            return { status: "failure", value: null, error: action.error, retries: state.retries };
    }
}

function useUserProfile(user: User | null, isAuthLoading: boolean) {
    const [state, dispatch] = useReducer(reducer<UserProfile | undefined | null>, {
        status: "init",
        value: null,
        retries: 0,
    } as State<UserProfile | undefined | null>);
    const hookId = useRef(Math.random().toString(36).substring(2, 8));

    const snapshotListener = useRef<Unsubscribe | null>(null);
    // Sometimes the permission is denied while signing in. Until the cause is found, retry the user profile fetch

    const subscribeToUserProfile = useEffectEvent((uid: string) => {
        const db = getFirestoreDB();
        const userCollectionRef = collection(db, "userProfile").withConverter(userProfileConverter);
        const userProfileRef = doc(userCollectionRef, uid);
        if (!user) {
            throw new Error("No user provided to subscribeToUserProfile");
        }

        const unsubscribe = onSnapshot(
            userProfileRef,
            (snapshot) => {
                dispatch({ type: "success", value: snapshot.exists() ? snapshot.data() : null });
            },
            (error) => {
                console.error("Subscription rejected for user profile:", uid);
                console.error(error);
                // Delay dispatch to give some time for transient errors to resolve
                setTimeout(() => dispatch({ type: "error", error }), 1000);
            }
        );
        return unsubscribe;
    });

    useEffect(() => {
        if (!user || user.isAnonymous) return;
        const uid = user?.uid;

        if (isAuthLoading) return;

        if (state.retries >= 3) {
            dispatch({ type: "failure", error: new Error("No user or max retries reached") });
            return;
        }

        if (["success", "failure", "loading"].includes(state.status)) return;

        dispatch({ type: "start" });
        if (snapshotListener.current) {
            snapshotListener.current();
        }
        const unsubscribe = subscribeToUserProfile(uid);
        snapshotListener.current = unsubscribe;
    }, [isAuthLoading, state.retries, state.status, user]);

    const isUserProfileLoading = state.status === "loading" || state.status === "error";

    return { userProfile: state.value, isUserProfileLoading };
}

export default useUserProfile;
